import { useEffect } from 'react'

import { ActionIconEnum } from 'common/enums/ActionIcon'
import { ReceiptStatusEnum } from 'common/enums/ReceiptStatus'
import { useNavBar } from 'hooks/useNavBar'
import { useHistory } from 'react-router-dom'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { AlertSign, SuccessSign } from 'styles/commons'
import { formatWeekDayDate } from 'utils/commons'
import { Icons } from 'utils/icons'
import { paymentDictionary, PaymentTypeEnum } from 'common/enums/PaymentTypeEnum'
import RaffleImages from 'assets/png'
import { TabIdEnum } from 'common/enums/TabIdEnum'

import { Button } from '@interco/inter-ui/components/Button'
import { Flex } from '@interco/inter-ui/components/Flex'
import { Tag } from '@interco/inter-ui/components/Tag'
import { Text } from '@interco/inter-ui/components/Text'
import { formatToBRL } from '@interco/lib-util'

import * as S from './Receipt.styles'
import { analyticsReceiptTag } from './Receipt.tags'

export const Receipt = () => {
  const history = useHistory()
  const checkoutResponse = useBoundState((state) => state.checkoutResponse)
  const getTickets = useBoundState((state) => state.getTickets)
  const setSelectedTab = useBoundState((state) => state.setSelectedTab)
  const contentTitle = useBoundState((state) => state.contentTitle)
  const contentMyTitle = useBoundState((state) => state.contentMyTitle)

  useNavBar({
    actionIcons: {
      rightIcon: ActionIconEnum.HOME,
    },
  })

  const receiptIcon = {
    success: <Icons.CheckCircle width="32px" height="32px" color="var(--neutral-theme)" />,
    pending: <Icons.Clock width="32px" height="32px" color="black" />,
  }

  const SUCCESS_MSG = 'Você já está participando dos sorteios!'
  const PROCESSING_MSG = `${contentTitle.value1} em processamento`
  const IS_SUCCESS = checkoutResponse.status === ReceiptStatusEnum.SUCCESS
  const IS_LOYALTY = checkoutResponse.saleInfo.paymentType === PaymentTypeEnum.LOYALTY
  const totalAmount = formatToBRL(checkoutResponse.saleInfo.amount)
  const paymentMethod = paymentDictionary[checkoutResponse.saleInfo.paymentType]

  useEffect(() => {
    analyticsReceiptTag({
      ref_type: IS_SUCCESS ? 'sucesso' : 'pedente',
      action_id: IS_SUCCESS ? SUCCESS_MSG : PROCESSING_MSG,
      value: totalAmount,
      payment: paymentMethod,
    })
  }, [])

  useEffect(() => {
    getTickets()
  }, [])

  const receiptDataView = [
    {
      label: IS_SUCCESS ? 'Quantidade' : 'Status',
      value: IS_SUCCESS ? (
        checkoutResponse.saleInfo?.quantity
      ) : (
        <S.TagContent>
          <Tag theme="yellow">Em processamento</Tag>
        </S.TagContent>
      ),
    },
    {
      label: IS_LOYALTY ? 'Total de pontos loop' : 'Valor',
      value: IS_LOYALTY ? checkoutResponse.saleInfo.amount : totalAmount,
    },
    {
      label: 'Forma de pagamento',
      value: paymentMethod,
    },
    {
      label: IS_SUCCESS ? 'Data do pagamento' : 'Data de solicitação',
      value: IS_SUCCESS
        ? formatWeekDayDate(checkoutResponse.saleInfo.paymentDate)
        : formatWeekDayDate(checkoutResponse.saleInfo.requestDate),
    },
  ]

  const renderSuccess = (
    <S.Header>
      <SuccessSign>{receiptIcon.success}</SuccessSign>
      <Text variant="headline-h2" as="h2" semiBold>
        {SUCCESS_MSG}
      </Text>
    </S.Header>
  )

  const renderProcess = (
    <S.Header>
      <AlertSign>{receiptIcon.pending}</AlertSign>
      <Text variant="headline-h2" as="h2" semiBold>
        {PROCESSING_MSG}
      </Text>
      <Text variant="body-3" as="p">
        Em breve, você receberá uma notificação com a confirmação da compra.
      </Text>
    </S.Header>
  )

  const renderData = (
    <S.Data>
      {receiptDataView.map((item) => (
        <Flex key={item.label} direction="row">
          <Text variant="caption-1">{item.label}</Text>
          <Text variant="caption-1" bold colorWeight={500}>
            {item.value}
          </Text>
        </Flex>
      ))}
    </S.Data>
  )

  const handleStartGame = () => {
    history.push(TypesRoutes.START)
  }

  const handleAfterSales = () => {
    setSelectedTab(TabIdEnum.MY_TICKETS)
    history.push(TypesRoutes.AFTER_SALES)
  }

  const handleGoHome = () => {
    history.push(TypesRoutes.LANDING_PAGE_HOME)
  }

  const renderLuckCard = (
    <S.LuckCard>
      <Flex direction="row" gap="16px" alignItems="center">
        {RaffleImages.receiptGift}

        {IS_SUCCESS ? (
          <Flex direction="column" gap="8px" onClick={handleStartGame}>
            <Text variant="caption-1" colorWeight={500}>
              Descubra se você ganhou até R$ 100,00 na hora!
            </Text>
            <Button variant="link">Raspar agora</Button>
          </Flex>
        ) : (
          <Flex direction="column" gap="8px" onClick={handleAfterSales}>
            <Text variant="caption-1" colorWeight={500}>
              Seu {contentTitle.value1Lowercase} estará pronto em minutos! Acompanhe:
            </Text>
            <Button variant="link">Acessar {contentMyTitle.value2Lowercase}</Button>
          </Flex>
        )}
      </Flex>
    </S.LuckCard>
  )

  const renderStickFooter = (
    <S.Footer>
      <Button fullWidth variant="secondary" onClick={handleGoHome}>
        Voltar para a home
      </Button>
    </S.Footer>
  )

  const renderHeader: Record<ReceiptStatusEnum, JSX.Element | undefined> = {
    SUCCESS: renderSuccess,
    PROCESSING: renderProcess,
    ERROR: undefined,
  }

  return (
    <S.Container>
      {renderHeader[checkoutResponse.status]}
      {renderData}
      {renderLuckCard}
      {renderStickFooter}
    </S.Container>
  )
}
